import { SchemaField, FieldType } from './types';

let _idCounter = 0;

export function newId(): string {
  return `sf_${++_idCounter}_${Date.now()}`;
}

export function createEmptyField(): SchemaField {
  return {
    id: newId(),
    name: '',
    type: 'string',
    required: false,
    description: '',
    format: '',
    enumValues: '',
    minimum: '',
    maximum: '',
    children: [],
    arrayItemType: 'string',
  };
}

export function fieldToSchema(field: SchemaField): object {
  const schema: Record<string, any> = {};

  switch (field.type) {
    case 'string':
      schema.type = 'string';
      if (field.format) schema.format = field.format;
      if (field.enumValues.trim()) {
        schema.enum = field.enumValues
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }
      break;
    case 'number':
    case 'integer':
      schema.type = field.type;
      if (field.minimum !== '') {
        const min = Number(field.minimum);
        if (Number.isFinite(min)) schema.minimum = min;
      }
      if (field.maximum !== '') {
        const max = Number(field.maximum);
        if (Number.isFinite(max)) schema.maximum = max;
      }
      break;
    case 'boolean':
      schema.type = 'boolean';
      break;
    case 'object':
      schema.type = 'object';
      if (field.children.length > 0) {
        schema.properties = {};
        const req: string[] = [];
        for (const child of field.children) {
          if (child.name) {
            schema.properties[child.name] = fieldToSchema(child);
            if (child.required) req.push(child.name);
          }
        }
        if (req.length) schema.required = req;
      }
      break;
    case 'array':
      schema.type = 'array';
      if (field.arrayItemType === 'object' && field.children.length > 0) {
        const itemProps: Record<string, object> = {};
        const itemReq: string[] = [];
        for (const child of field.children) {
          if (child.name) {
            itemProps[child.name] = fieldToSchema(child);
            if (child.required) itemReq.push(child.name);
          }
        }
        schema.items = { type: 'object', properties: itemProps };
        if (itemReq.length) schema.items.required = itemReq;
      } else {
        schema.items = { type: field.arrayItemType || 'string' };
      }
      break;
  }

  if (field.description) schema.description = field.description;
  return schema;
}

export function fieldsToJsonSchema(fields: SchemaField[]): object {
  const properties: Record<string, object> = {};
  const required: string[] = [];

  for (const field of fields) {
    if (!field.name) continue;
    properties[field.name] = fieldToSchema(field);
    if (field.required) required.push(field.name);
  }

  const schema: Record<string, any> = { type: 'object', properties };
  if (required.length) schema.required = required;
  return schema;
}

export function schemaToField(
  name: string,
  schema: Record<string, any>,
  isRequired: boolean,
): SchemaField {
  const field = createEmptyField();
  field.name = name;
  field.required = isRequired;
  field.description = schema.description || '';

  // Handle union types like ["number", "null"] — pick first non-null type
  let type: string = schema.type || 'string';
  if (Array.isArray(type)) {
    type = type.find((t: string) => t !== 'null') || 'string';
  }

  switch (type) {
    case 'string':
      field.type = 'string';
      field.format = schema.format || '';
      if (Array.isArray(schema.enum)) {
        field.enumValues = schema.enum.join(',');
      }
      break;
    case 'number':
    case 'integer':
      field.type = type;
      if (schema.minimum != null) field.minimum = String(schema.minimum);
      if (schema.maximum != null) field.maximum = String(schema.maximum);
      break;
    case 'boolean':
      field.type = 'boolean';
      break;
    case 'object':
      field.type = 'object';
      if (schema.properties) {
        const childRequired = Array.isArray(schema.required)
          ? schema.required
          : [];
        field.children = Object.entries(schema.properties).map(([k, v]) =>
          schemaToField(k, v as Record<string, any>, childRequired.includes(k)),
        );
      }
      break;
    case 'array':
      field.type = 'array';
      const itemSchema = schema.items;
      const itemType = Array.isArray(itemSchema?.type)
        ? itemSchema.type.find((t: string) => t !== 'null') || 'string'
        : itemSchema?.type || 'string';
      const supportedItemTypes = ['string', 'number', 'integer', 'boolean'];
      if (itemType === 'object' && itemSchema?.properties) {
        field.arrayItemType = 'object';
        const childRequired = Array.isArray(itemSchema.required)
          ? itemSchema.required
          : [];
        field.children = Object.entries(itemSchema.properties).map(([k, v]) =>
          schemaToField(k, v as Record<string, any>, childRequired.includes(k)),
        );
      } else {
        field.arrayItemType = supportedItemTypes.includes(itemType)
          ? itemType
          : 'string';
      }
      break;
  }

  return field;
}

export function parseJsonSchemaToFields(schema: any): SchemaField[] | null {
  try {
    if (
      !schema ||
      typeof schema !== 'object' ||
      schema.type !== 'object' ||
      !schema.properties
    )
      return null;
    const req = Array.isArray(schema.required) ? schema.required : [];
    return Object.entries(schema.properties).map(([name, def]) =>
      schemaToField(name, def as Record<string, any>, req.includes(name)),
    );
  } catch {
    return null;
  }
}

/**
 * Merge a new schema-generated sample with an existing sample, preserving
 * existing values for fields whose type still matches. Adds defaults for
 * newly added fields and drops fields no longer in the schema.
 */
export function mergeSampleWithSchema(
  fields: SchemaField[],
  existingSample?: object,
): object {
  if (!existingSample || typeof existingSample !== 'object') {
    return generateSampleFromSchema(fields);
  }
  return mergeFields(fields, existingSample as Record<string, any>);
}

function mergeFields(
  fields: SchemaField[],
  existing: Record<string, any>,
): Record<string, any> {
  const merged: Record<string, any> = {};
  for (const field of fields) {
    if (!field.name) continue;
    merged[field.name] = mergeFieldValue(field, existing[field.name]);
  }
  return merged;
}

function mergeFieldValue(field: SchemaField, existing: any): any {
  switch (field.type) {
    case 'string':
      return typeof existing === 'string'
        ? existing
        : generateSampleForField(field);
    case 'number':
      return typeof existing === 'number'
        ? existing
        : generateSampleForField(field);
    case 'integer':
      return typeof existing === 'number' && Number.isInteger(existing)
        ? existing
        : generateSampleForField(field);
    case 'boolean':
      return typeof existing === 'boolean'
        ? existing
        : generateSampleForField(field);
    case 'object':
      return existing &&
        typeof existing === 'object' &&
        !Array.isArray(existing)
        ? mergeFields(field.children, existing as Record<string, any>)
        : generateSampleForField(field);
    case 'array':
      return Array.isArray(existing) ? existing : generateSampleForField(field);
  }
}

function generateSampleForField(field: SchemaField): any {
  switch (field.type) {
    case 'string':
      if (field.format === 'date-time') return '2024-01-01T00:00:00Z';
      if (field.format === 'date') return '2024-01-01';
      if (field.format === 'time') return '12:00:00';
      if (field.format === 'email') return 'user@example.com';
      if (field.format === 'uri') return 'https://example.com';
      if (field.format === 'uuid')
        return '00000000-0000-0000-0000-000000000000';
      if (field.format === 'ipv4') return '192.168.1.1';
      if (field.enumValues.trim()) {
        const first = field.enumValues.split(',')[0]?.trim();
        return first || 'example';
      }
      return 'example';
    case 'number': {
      const min = Number(field.minimum);
      const max = Number(field.maximum);
      const base = field.minimum !== '' && Number.isFinite(min) ? min : 1;
      // Clamp to maximum so the default (1) can't violate a `maximum < 1` schema.
      return field.maximum !== '' && Number.isFinite(max) && max < base
        ? max
        : base;
    }
    case 'integer': {
      const min = Number(field.minimum);
      const max = Number(field.maximum);
      const base =
        field.minimum !== '' && Number.isFinite(min) ? Math.ceil(min) : 1;
      return field.maximum !== '' &&
        Number.isFinite(max) &&
        Math.floor(max) < base
        ? Math.floor(max)
        : base;
    }
    case 'boolean':
      return true;
    case 'object':
      return generateSampleFromSchema(field.children);
    case 'array':
      if (field.arrayItemType === 'object' && field.children.length > 0) {
        return [generateSampleFromSchema(field.children)];
      }
      return [];
  }
}

export function generateSampleFromSchema(fields: SchemaField[]): object {
  const sample: Record<string, any> = {};
  for (const field of fields) {
    if (!field.name) continue;
    sample[field.name] = generateSampleForField(field);
  }
  return sample;
}

export function inferSchemaFromSample(obj: any): object {
  if (obj === null) return { type: 'null' };
  if (Array.isArray(obj)) {
    const itemSchema = obj.length > 0 ? inferSchemaFromSample(obj[0]) : {};
    return { type: 'array', items: itemSchema };
  }
  if (typeof obj === 'object') {
    const properties: Record<string, object> = {};
    for (const [key, value] of Object.entries(obj)) {
      properties[key] = inferSchemaFromSample(value);
    }
    const schema: Record<string, any> = { type: 'object', properties };
    return schema;
  }
  if (typeof obj === 'number') {
    return Number.isInteger(obj) ? { type: 'integer' } : { type: 'number' };
  }
  if (typeof obj === 'boolean') return { type: 'boolean' };
  return { type: 'string' };
}
